import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Edit2, XCircle, Building2 } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import StatusBadge from '../components/StatusBadge';
import LoadingSpinner from '../components/LoadingSpinner';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { getLoadDetail, assignDriver, editLoad, cancelLoad } from '../api/loadsApi';
import { createVehicle } from '../api/vehiclesApi';
import { getDrivers } from '../api/driversApi';
import { getFullPhotoUrl } from '../utils/imageUrl';
import { useToast } from '../context/ToastContext';

function LoadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [load, setLoad] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [newVehicle, setNewVehicle] = useState({ vehicle_name: '', vehicle_model: '', vin_number: '' });
  const [savingVehicle, setSavingVehicle] = useState(false);

  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [originDetails, setOriginDetails] = useState(null);
  const [destinationDetails, setDestinationDetails] = useState(null);
  const [calculatingDistance, setCalculatingDistance] = useState(false);

  const fetchLoad = async () => {
    try {
      const result = await getLoadDetail(id);
      if (result.success) {
        setLoad(result.load);
      } else {
        showToast('error', result.message || 'Failed to load data.');
      }
    } catch (err) {
      showToast('error', 'Could not connect to server.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLoad();
    getDrivers(1, 50).then((result) => {
      if (result.success) setDrivers(result.drivers);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (
      originDetails?.lat &&
      originDetails?.lng &&
      destinationDetails?.lat &&
      destinationDetails?.lng &&
      window.google
    ) {
      setCalculatingDistance(true);

      const service = new window.google.maps.DistanceMatrixService();

      service.getDistanceMatrix(
        {
          origins: [{ lat: originDetails.lat, lng: originDetails.lng }],
          destinations: [{ lat: destinationDetails.lat, lng: destinationDetails.lng }],
          travelMode: window.google.maps.TravelMode.DRIVING,
          unitSystem: window.google.maps.UnitSystem.IMPERIAL,
        },
        (response, status) => {
          setCalculatingDistance(false);
          if (status === 'OK') {
            const result = response.rows[0]?.elements[0];
            if (result?.status === 'OK') {
              setEditForm((prev) => ({ ...prev, distance: result.distance.text }));
            }
          }
        }
      );
    }
  }, [originDetails, destinationDetails]);

  const handleDriverChange = async (e) => {
    const driverId = e.target.value || null;
    const result = await assignDriver(load.id_load, driverId);
    if (result.success) {
      showToast('success', 'Driver updated.');
      fetchLoad();
    } else {
      showToast('error', result.message || 'Failed to assign driver.');
    }
  };

  const handleAddVehicle = async (e) => {
    e.preventDefault();
    setSavingVehicle(true);
    try {
      const result = await createVehicle({ load_id: load.id_load, ...newVehicle });
      if (result.success) {
        showToast('success', 'Vehicle added successfully.');
        setNewVehicle({ vehicle_name: '', vehicle_model: '', vin_number: '' });
        setShowAddVehicle(false);
        fetchLoad();
      } else {
        showToast('error', result.message || 'Failed to add vehicle.');
      }
    } catch (err) {
      showToast('error', 'Could not connect to server.');
    } finally {
      setSavingVehicle(false);
    }
  };

  const startEdit = () => {
    setEditForm({
      origin: load.origin,
      destination: load.destination,
      distance: load.distance || '',
      price_value: load.price_value,
      price_currency: load.price_currency,
      pick_up_date: load.pick_up_date,
      delivery_date: load.delivery_date,
    });
    setOriginDetails(null);
    setDestinationDetails(null);
    setEditMode(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      const result = await editLoad({ id_load: load.id_load, ...editForm });
      if (result.success) {
        showToast('success', 'Load updated successfully.');
        setEditMode(false);
        fetchLoad();
      } else {
        showToast('error', result.message || 'Failed to update load.');
      }
    } catch (err) {
      showToast('error', 'Could not connect to server.');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleCancelLoad = async () => {
    if (!window.confirm('Are you sure you want to cancel this load? This cannot be undone.')) return;

    const result = await cancelLoad(load.id_load);
    if (result.success) {
      showToast('success', 'Load canceled.');
      fetchLoad();
    } else {
      showToast('error', result.message || 'Failed to cancel load.');
    }
  };

  if (loading || !load) {
    return (
      <div style={{ display: 'flex' }}>
        <Sidebar />
        <div style={{ flex: 1 }}>
          <TopBar title="Loading..." />
          <LoadingSpinner fullScreen />
        </div>
      </div>
    );
  }

  const isCanceled = load.load_status === 'canceled';

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <div style={{ flex: 1 }}>
        <TopBar
          title={`Load #${load.id_load}`}
          rightAction={
            !isCanceled && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={startEdit} style={styles.editBtn}>
                  <Edit2 size={14} style={{ marginRight: 6 }} /> Edit
                </button>
                <button onClick={handleCancelLoad} style={styles.cancelLoadBtn}>
                  <XCircle size={14} style={{ marginRight: 6 }} /> Cancel Load
                </button>
              </div>
            )
          }
        />

        <div style={{ padding: 24, maxWidth: 640 }}>
          {isCanceled && <div style={styles.canceledBanner}>This load has been canceled.</div>}

          {/* Linked client / company info — read-only here, managed from the Clients section */}
          {load.client_name && (
            <div
              style={styles.clientCard}
              onClick={() => navigate(`/clients/${load.client_id}`)}
            >
              <Building2 size={18} color="#2563eb" style={{ marginRight: 10, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={styles.clientName}>{load.client_name}</div>
                {load.client_contact_name && (
                  <div style={styles.clientContact}>
                    {load.client_contact_name}
                    {load.client_contact_phone ? ` • ${load.client_contact_phone}` : ''}
                  </div>
                )}
              </div>
              <span style={styles.clientViewLink}>View client →</span>
            </div>
          )}

          {editMode ? (
            <form onSubmit={handleSaveEdit} style={styles.card}>
              <AddressAutocomplete
                label="Origin"
                value={editForm.origin}
                onChange={(val) => setEditForm({ ...editForm, origin: val })}
                onAddressSelect={setOriginDetails}
              />

              <div style={{ marginTop: 16 }}>
                <AddressAutocomplete
                  label="Destination"
                  value={editForm.destination}
                  onChange={(val) => setEditForm({ ...editForm, destination: val })}
                  onAddressSelect={setDestinationDetails}
                />
              </div>

              <label style={styles.label}>
                Distance{' '}
                {calculatingDistance && (
                  <span style={{ color: '#9ca3af', fontWeight: 400 }}>(calculating...)</span>
                )}
              </label>
              <input
                value={editForm.distance}
                onChange={(e) => setEditForm({ ...editForm, distance: e.target.value })}
                style={styles.input}
              />

              <label style={styles.label}>Price</label>
              <input
                type="number"
                step="0.01"
                value={editForm.price_value}
                onChange={(e) => setEditForm({ ...editForm, price_value: e.target.value })}
                style={styles.input}
                required
              />

              <label style={styles.label}>Pick Up Date</label>
              <input
                type="date"
                value={editForm.pick_up_date}
                onChange={(e) => setEditForm({ ...editForm, pick_up_date: e.target.value })}
                style={styles.input}
                required
              />

              <label style={styles.label}>Delivery Date</label>
              <input
                type="date"
                value={editForm.delivery_date}
                onChange={(e) => setEditForm({ ...editForm, delivery_date: e.target.value })}
                style={styles.input}
                required
              />

              <div style={styles.buttonRow}>
                <button type="button" onClick={() => setEditMode(false)} style={styles.cancelBtn}>
                  Cancel
                </button>
                <button type="submit" disabled={savingEdit} style={styles.submitBtn}>
                  {savingEdit ? '...' : 'Save Changes'}
                </button>
              </div>
            </form>
          ) : (
            <div style={styles.card}>
              <div style={styles.topRow}>
                <span style={styles.routeText}>
                  {load.origin} <span style={{ color: '#9ca3af' }}>→</span> {load.destination}
                </span>
                <StatusBadge status={load.load_status} />
              </div>
              <div style={styles.metaRow}>
                <span>{load.distance}</span>
                <span style={styles.priceText}>
                  {Number(load.price_value).toLocaleString()} {load.price_currency}
                </span>
              </div>
              <div style={styles.datesRow}>
                <div>
                  <div style={styles.dateLabel}>Pick up</div>
                  <div style={styles.dateValue}>{load.pick_up_date}</div>
                </div>
                <div>
                  <div style={styles.dateLabel}>Delivery</div>
                  <div style={styles.dateValue}>{load.delivery_date}</div>
                </div>
              </div>

              <label style={styles.label}>Assigned Driver</label>
              <select value={load.driver_assigned_id || ''} onChange={handleDriverChange} style={styles.input}>
                <option value="">Unassigned</option>
                {drivers.map((d) => (
                  <option key={d.id_driver} value={d.id_driver}>
                    {d.driver_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div style={styles.sectionHeader}>
            <span style={styles.sectionTitle}>
              Vehicles ({load.vehicles.filter((v) => v.vehicle_status !== 'unassigned').length})
            </span>
            {!isCanceled && (
              <button onClick={() => setShowAddVehicle(!showAddVehicle)} style={styles.addVehicleBtn}>
                <Plus size={14} style={{ marginRight: 4 }} /> Add Vehicle
              </button>
            )}
          </div>

          {showAddVehicle && (
            <form onSubmit={handleAddVehicle} style={styles.addVehicleForm}>
              <label style={styles.label}>Vehicle Make</label>
              <input
                value={newVehicle.vehicle_name}
                onChange={(e) => setNewVehicle({ ...newVehicle, vehicle_name: e.target.value })}
                placeholder="e.g. Toyota"
                style={styles.input}
                required
              />
              <label style={styles.label}>Vehicle Model</label>
              <input
                value={newVehicle.vehicle_model}
                onChange={(e) => setNewVehicle({ ...newVehicle, vehicle_model: e.target.value })}
                placeholder="e.g. Camry"
                style={styles.input}
                required
              />
              <label style={styles.label}>VIN Number</label>
              <input
                value={newVehicle.vin_number}
                onChange={(e) => setNewVehicle({ ...newVehicle, vin_number: e.target.value })}
                placeholder="17-character VIN"
                style={styles.input}
                required
              />
              <div style={styles.buttonRow}>
                <button type="button" onClick={() => setShowAddVehicle(false)} style={styles.cancelBtn}>
                  Cancel
                </button>
                <button type="submit" disabled={savingVehicle} style={styles.submitBtn}>
                  {savingVehicle ? '...' : 'Add Vehicle'}
                </button>
              </div>
            </form>
          )}

          {load.vehicles.length === 0 ? (
            <p style={{ color: '#9ca3af' }}>No vehicles added yet.</p>
          ) : (
            load.vehicles.map((vehicle) => {
              const photo = getFullPhotoUrl(vehicle.car_photos_url?.[0]);
              const isUnassigned = vehicle.vehicle_status === 'unassigned';
              return (
                <div
                  key={vehicle.id_vehicle}
                  onClick={() => navigate(`/vehicles/${vehicle.id_vehicle}`)}
                  style={{
                    ...styles.vehicleCard,
                    opacity: isUnassigned ? 0.5 : 1,
                  }}
                >
                  {photo ? (
                    <img src={photo} alt={vehicle.vehicle_name} style={styles.vehicleThumb} />
                  ) : (
                    <div style={styles.vehicleThumbPlaceholder}>🚗</div>
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={styles.vehicleName}>
                      {vehicle.vehicle_name} {vehicle.vehicle_model}
                    </div>
                    <div style={styles.vin}>VIN: {vehicle.vin_number}</div>
                  </div>
                  <StatusBadge status={vehicle.vehicle_status} />
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  card: { backgroundColor: 'white', borderRadius: 12, padding: 20, marginBottom: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  canceledBanner: { backgroundColor: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, marginBottom: 16 },
  clientCard: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#eff6ff',
    borderRadius: 12,
    padding: '14px 16px',
    marginBottom: 16,
    cursor: 'pointer',
  },
  clientName: { fontSize: 14, fontWeight: 700, color: '#111827' },
  clientContact: { fontSize: 12, color: '#6b7280', marginTop: 2 },
  clientViewLink: { fontSize: 12, fontWeight: 600, color: '#2563eb', flexShrink: 0 },
  topRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  routeText: { fontWeight: 700, fontSize: 16, color: '#111827' },
  metaRow: { display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#6b7280', marginBottom: 14 },
  priceText: { fontWeight: 700, color: '#2563eb', fontSize: 15 },
  datesRow: { display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #f3f4f6', paddingTop: 12, marginBottom: 14 },
  dateLabel: { fontSize: 11, color: '#9ca3af', marginBottom: 2 },
  dateValue: { fontSize: 14, fontWeight: 600, color: '#111827' },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, marginTop: 12 },
  input: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, outline: 'none', boxSizing: 'border-box' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: '#374151' },
  addVehicleBtn: { display: 'flex', alignItems: 'center', backgroundColor: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  addVehicleForm: { backgroundColor: 'white', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  buttonRow: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 },
  cancelBtn: { padding: '9px 18px', borderRadius: 8, border: '1px solid #e5e7eb', backgroundColor: 'white', color: '#374151', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  submitBtn: { padding: '9px 18px', borderRadius: 8, border: 'none', backgroundColor: '#2563eb', color: 'white', fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  editBtn: { display: 'flex', alignItems: 'center', backgroundColor: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  cancelLoadBtn: { display: 'flex', alignItems: 'center', backgroundColor: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  vehicleCard: { display: 'flex', alignItems: 'center', backgroundColor: 'white', borderRadius: 12, padding: 14, marginBottom: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', cursor: 'pointer' },
  vehicleThumb: { width: 50, height: 50, borderRadius: 8, objectFit: 'cover', marginRight: 12 },
  vehicleThumbPlaceholder: { width: 50, height: 50, borderRadius: 8, backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 12, fontSize: 22 },
  vehicleName: { fontWeight: 700, fontSize: 14, color: '#111827', marginBottom: 2 },
  vin: { fontSize: 12, color: '#6b7280' },
};

export default LoadDetail;